import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, List, ListItemButton,
  ListItemAvatar, ListItemText, Avatar, InputBase, CircularProgress,
  Badge, Box, Typography, IconButton, Tooltip, useTheme as useMuiTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import chatService from '../../services/chat.service';
import { UserSummary } from '../../types/chat.types';
import { useChat } from '../../contexts/ChatContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { chatTranslations } from './chatTranslations';

const AVATAR_COLORS = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#a18cd1,#fbc2eb)',
];
const avatarGradient = (name: string) =>
  AVATAR_COLORS[(name.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

interface Props {
  open: boolean;
  onClose: () => void;
}

const NewDirectDialog: React.FC<Props> = ({ open, onClose }) => {
  const { startDirect } = useChat();
  const muiTheme = useMuiTheme();
  const { language } = useLanguage();
  const t = chatTranslations[language];
  const isRTL = language === 'ar';

  const [users, setUsers]   = useState<UserSummary[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setLoading(true);
    chatService.getUsers().then(setUsers).finally(() => setLoading(false));
  }, [open]);

  const filtered = users.filter(u =>
    !search ||
    u.fullName.toLowerCase().includes(search.toLowerCase()) ||
    u.userName.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = async (userId: number) => {
    await startDirect(userId);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1600 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{
        background: muiTheme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3a5c 100%)'
          : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        px: 2.5, py: 2,
        display: 'flex', alignItems: 'center', gap: 1,
        direction: isRTL ? 'rtl' : 'ltr',
      }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, color: 'white', fontSize: '0.95rem' }}>
          {t.newMessage}
        </Typography>
        <Tooltip title={t.close}>
          <IconButton size="small" onClick={onClose}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ p: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
        {/* Search */}
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'action.hover', borderRadius: 3, px: 1.5, py: 0.75,
            border: '1.5px solid', borderColor: 'divider',
            '&:focus-within': { borderColor: 'primary.main', bgcolor: 'background.paper' },
            transition: 'all 0.2s',
          }}>
            <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
            <InputBase
              fullWidth
              autoFocus
              placeholder={t.searchUsers}
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ fontSize: '0.85rem' }}
            />
          </Box>
        </Box>

        {/* User list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1, color: 'text.disabled' }}>
            <PersonIcon sx={{ fontSize: 40, opacity: 0.35 }} />
            <Typography variant="caption">{t.noUsersFound}</Typography>
          </Box>
        ) : (
          <List dense sx={{ px: 1, pb: 1.5 }}>
            {filtered.map(u => (
              <ListItemButton
                key={u.userId}
                onClick={() => handleSelect(u.userId)}
                sx={{
                  borderRadius: 2, mb: 0.25,
                  '&:hover': { bgcolor: alpha(muiTheme.palette.primary.main, 0.08) },
                  transition: 'background 0.15s',
                }}
              >
                <ListItemAvatar>
                  <Badge
                    variant="dot"
                    color={u.isOnline ? 'success' : 'default'}
                    anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
                    sx={{ '& .MuiBadge-dot': { width: 12, height: 12, border: '2px solid', borderColor: 'background.paper', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' } }}
                  >
                    <Avatar sx={{
                      width: 36, height: 36, fontSize: 14, fontWeight: 700,
                      background: avatarGradient(u.fullName),
                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    }}>
                      {u.fullName.charAt(0).toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={u.fullName}
                  secondary={u.userName}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.84rem' }}
                  secondaryTypographyProps={{ variant: 'caption', fontSize: '0.72rem' }}
                />
                {u.isOnline && (
                  <Box sx={{
                    fontSize: '0.62rem', color: 'success.main', fontWeight: 600,
                    bgcolor: alpha(muiTheme.palette.success.main, 0.12), px: 0.75, py: 0.25, borderRadius: 1,
                  }}>
                    {t.online}
                  </Box>
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewDirectDialog;
